/**
 * Simplex Noise implementation for procedural generation
 * Used for organic cell shapes, animation variation, and natural-looking randomness
 *
 * Based on Stefan Gustavson's simplex noise algorithm
 */

// Gradient vectors for 2D
const GRAD_2D = [
  [1, 1], [-1, 1], [1, -1], [-1, -1],
  [1, 0], [-1, 0], [0, 1], [0, -1],
];

// Gradient vectors for 3D
const GRAD_3D = [
  [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
  [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
  [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
];

// Skewing factors for 2D
const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;

// Skewing factors for 3D
const F3 = 1 / 3;
const G3 = 1 / 6;

/**
 * Simplex noise generator with seeded randomization
 */
export class SimplexNoise {
  private perm: Uint8Array;
  private permMod12: Uint8Array;

  /**
   * Create a new SimplexNoise generator
   * @param seed - Seed value for reproducible noise (default: random)
   */
  constructor(seed?: number) {
    this.perm = new Uint8Array(512);
    this.permMod12 = new Uint8Array(512);

    const actualSeed = seed ?? Math.random() * 2147483647;
    this.initPermutation(actualSeed);
  }

  /**
   * Initialize permutation table from seed
   */
  private initPermutation(seed: number): void {
    // Create initial permutation array
    const p = new Uint8Array(256);
    for (let i = 0; i < 256; i++) {
      p[i] = i;
    }

    // Shuffle using seeded random
    let s = seed;
    for (let i = 255; i > 0; i--) {
      s = this.nextRandom(s);
      const j = s % (i + 1);
      [p[i], p[j]] = [p[j], p[i]];
    }

    // Duplicate permutation table
    for (let i = 0; i < 512; i++) {
      this.perm[i] = p[i & 255];
      this.permMod12[i] = this.perm[i] % 12;
    }
  }

  /**
   * Simple seeded random number generator (LCG)
   */
  private nextRandom(seed: number): number {
    return (seed * 1103515245 + 12345) & 0x7fffffff;
  }

  /**
   * 2D Simplex noise
   * @param x - X coordinate
   * @param y - Y coordinate
   * @returns Noise value between -1 and 1
   */
  noise2D(x: number, y: number): number {
    // Skew input space to determine simplex cell
    const s = (x + y) * F2;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);

    // Unskew back to get simplex cell origin
    const t = (i + j) * G2;
    const X0 = i - t;
    const Y0 = j - t;

    // Position within cell
    const x0 = x - X0;
    const y0 = y - Y0;

    // Determine which simplex we're in
    let i1: number, j1: number;
    if (x0 > y0) {
      i1 = 1;
      j1 = 0;
    } else {
      i1 = 0;
      j1 = 1;
    }

    // Offsets for corners
    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;

    // Hash coordinates
    const ii = i & 255;
    const jj = j & 255;

    // Calculate contributions from corners
    let n0 = 0, n1 = 0, n2 = 0;

    // Corner 0
    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 >= 0) {
      t0 *= t0;
      const gi0 = this.perm[ii + this.perm[jj]] % 8;
      n0 = t0 * t0 * this.dot2D(GRAD_2D[gi0], x0, y0);
    }

    // Corner 1
    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 >= 0) {
      t1 *= t1;
      const gi1 = this.perm[ii + i1 + this.perm[jj + j1]] % 8;
      n1 = t1 * t1 * this.dot2D(GRAD_2D[gi1], x1, y1);
    }

    // Corner 2
    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 >= 0) {
      t2 *= t2;
      const gi2 = this.perm[ii + 1 + this.perm[jj + 1]] % 8;
      n2 = t2 * t2 * this.dot2D(GRAD_2D[gi2], x2, y2);
    }

    // Scale to [-1, 1]
    return 70 * (n0 + n1 + n2);
  }

  /**
   * 3D Simplex noise (useful for time-based animation)
   * @param x - X coordinate
   * @param y - Y coordinate
   * @param z - Z coordinate (often time)
   * @returns Noise value between -1 and 1
   */
  noise3D(x: number, y: number, z: number): number {
    // Skew input space
    const s = (x + y + z) * F3;
    const i = Math.floor(x + s);
    const j = Math.floor(y + s);
    const k = Math.floor(z + s);

    // Unskew
    const t = (i + j + k) * G3;
    const X0 = i - t;
    const Y0 = j - t;
    const Z0 = k - t;

    // Position within cell
    const x0 = x - X0;
    const y0 = y - Y0;
    const z0 = z - Z0;

    // Determine simplex
    let i1: number, j1: number, k1: number;
    let i2: number, j2: number, k2: number;

    if (x0 >= y0) {
      if (y0 >= z0) {
        i1 = 1; j1 = 0; k1 = 0;
        i2 = 1; j2 = 1; k2 = 0;
      } else if (x0 >= z0) {
        i1 = 1; j1 = 0; k1 = 0;
        i2 = 1; j2 = 0; k2 = 1;
      } else {
        i1 = 0; j1 = 0; k1 = 1;
        i2 = 1; j2 = 0; k2 = 1;
      }
    } else {
      if (y0 < z0) {
        i1 = 0; j1 = 0; k1 = 1;
        i2 = 0; j2 = 1; k2 = 1;
      } else if (x0 < z0) {
        i1 = 0; j1 = 1; k1 = 0;
        i2 = 0; j2 = 1; k2 = 1;
      } else {
        i1 = 0; j1 = 1; k1 = 0;
        i2 = 1; j2 = 1; k2 = 0;
      }
    }

    // Offsets for corners
    const x1 = x0 - i1 + G3;
    const y1 = y0 - j1 + G3;
    const z1 = z0 - k1 + G3;
    const x2 = x0 - i2 + 2 * G3;
    const y2 = y0 - j2 + 2 * G3;
    const z2 = z0 - k2 + 2 * G3;
    const x3 = x0 - 1 + 3 * G3;
    const y3 = y0 - 1 + 3 * G3;
    const z3 = z0 - 1 + 3 * G3;

    // Hash coordinates
    const ii = i & 255;
    const jj = j & 255;
    const kk = k & 255;

    // Calculate contributions
    let n0 = 0, n1 = 0, n2 = 0, n3 = 0;

    // Corner 0
    let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
    if (t0 >= 0) {
      t0 *= t0;
      const gi0 = this.permMod12[ii + this.perm[jj + this.perm[kk]]];
      n0 = t0 * t0 * this.dot3D(GRAD_3D[gi0], x0, y0, z0);
    }

    // Corner 1
    let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
    if (t1 >= 0) {
      t1 *= t1;
      const gi1 = this.permMod12[ii + i1 + this.perm[jj + j1 + this.perm[kk + k1]]];
      n1 = t1 * t1 * this.dot3D(GRAD_3D[gi1], x1, y1, z1);
    }

    // Corner 2
    let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
    if (t2 >= 0) {
      t2 *= t2;
      const gi2 = this.permMod12[ii + i2 + this.perm[jj + j2 + this.perm[kk + k2]]];
      n2 = t2 * t2 * this.dot3D(GRAD_3D[gi2], x2, y2, z2);
    }

    // Corner 3
    let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
    if (t3 >= 0) {
      t3 *= t3;
      const gi3 = this.permMod12[ii + 1 + this.perm[jj + 1 + this.perm[kk + 1]]];
      n3 = t3 * t3 * this.dot3D(GRAD_3D[gi3], x3, y3, z3);
    }

    // Scale to [-1, 1]
    return 32 * (n0 + n1 + n2 + n3);
  }

  /**
   * 2D dot product with gradient
   */
  private dot2D(g: number[], x: number, y: number): number {
    return g[0] * x + g[1] * y;
  }

  /**
   * 3D dot product with gradient
   */
  private dot3D(g: number[], x: number, y: number, z: number): number {
    return g[0] * x + g[1] * y + g[2] * z;
  }

  /**
   * Generate noise in range [0, 1] instead of [-1, 1]
   */
  noise2DNormalized(x: number, y: number): number {
    return (this.noise2D(x, y) + 1) / 2;
  }

  /**
   * Generate noise in range [0, 1] instead of [-1, 1]
   */
  noise3DNormalized(x: number, y: number, z: number): number {
    return (this.noise3D(x, y, z) + 1) / 2;
  }

  /**
   * Fractal Brownian Motion (fBm) for more complex patterns
   * @param x - X coordinate
   * @param y - Y coordinate
   * @param octaves - Number of noise layers
   * @param persistence - How much each octave contributes
   * @param lacunarity - How much detail increases each octave
   */
  fbm2D(
    x: number,
    y: number,
    octaves: number = 4,
    persistence: number = 0.5,
    lacunarity: number = 2
  ): number {
    let total = 0;
    let frequency = 1;
    let amplitude = 1;
    let maxValue = 0;

    for (let i = 0; i < octaves; i++) {
      total += this.noise2D(x * frequency, y * frequency) * amplitude;
      maxValue += amplitude;
      amplitude *= persistence;
      frequency *= lacunarity;
    }

    return total / maxValue;
  }
}
